import React, { useState, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Orb } from "../components/Orb";
import { Volume2, ArrowRight, Loader2, Check } from "lucide-react";

const ANNA_AVATAR = "https://images.unsplash.com/photo-1777927519810-5feb98777717?crop=entropy&cs=srgb&fm=jpg&q=85&w=200";
const SAM_AVATAR = "https://images.unsplash.com/photo-1656947296515-3faa76840890?crop=entropy&cs=srgb&fm=jpg&q=85&w=200";

export default function Onboarding() {
  const { user, updateUser } = useAuth();
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [hint, setHint] = useState("");
  const [assistant, setAssistant] = useState("anna");
  const [previewing, setPreviewing] = useState(false);
  const [orbState, setOrbState] = useState("idle");
  const [saving, setSaving] = useState(false);
  const audioRef = useRef(null);

  const previewName = async () => {
    if (!name.trim()) return;
    setPreviewing(true); setOrbState("thinking");
    try {
      await api.put("/profile", { preferred_name: name.trim(), pronunciation_hint: hint.trim() });
      const { data } = await api.post("/tts", { text: `Nice to meet you, ${name.trim()}.`, assistant });
      setOrbState("speaking");
      const audio = new Audio(data.audio);
      audioRef.current = audio;
      audio.onended = () => setOrbState("idle");
      await audio.play();
    } catch {
      setOrbState("idle");
    } finally {
      setPreviewing(false);
    }
  };

  const finish = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", {
        preferred_name: name.trim(), pronunciation_hint: hint.trim(),
        assistant_preference: assistant, onboarded: true,
      });
      updateUser(data.user);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="stealth-bg min-h-screen flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-lg glass-panel rounded-3xl p-8 sm:p-10 text-center fade-in-up">
        <div className="flex justify-center mb-6">
          <Orb state={orbState} size={190} accent={assistant === "sam" ? "#D6D6D6" : "#E4E4E4"} />
        </div>

        {step === 0 && (
          <div className="fade-in-up">
            <div className="label-mono">Stealth Assistance</div>
            <h1 className="font-heading text-3xl font-medium mt-3">Hi, I'm {assistant === "sam" ? "Sam" : "Anna"}.</h1>
            <p className="text-slate-400 mt-3">What should I call you?</p>
            <input
              data-testid="onboarding-name-input"
              autoFocus value={name} onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && name.trim() && setStep(1)}
              placeholder="Your name"
              className="mt-6 w-full text-center rounded-xl bg-[#0D0D0D] border border-white/10 px-4 py-3 text-lg outline-none focus:border-mono-400/60"
            />
            <button
              data-testid="onboarding-name-next"
              disabled={!name.trim()} onClick={() => setStep(1)}
              className="mt-6 w-full rounded-full py-3 font-medium text-[#000000] flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ background: "linear-gradient(90deg, #FFFFFF, #C8C8C8)" }}
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {step === 1 && (
          <div className="fade-in-up">
            <h2 className="font-heading text-2xl font-medium">Nice to meet you, {name}.</h2>
            <p className="text-slate-400 mt-3">Here's how I'll say your name. If it's off, add a spelling that sounds right.</p>

            <button
              data-testid="onboarding-preview-name"
              onClick={previewName} disabled={previewing}
              className="mt-6 inline-flex items-center gap-2 rounded-full px-5 py-2.5 glass-card text-mono-300 hover:border-mono-400/50 transition-all"
            >
              {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4" />}
              Preview my name
            </button>

            <div className="mt-6 text-left">
              <label className="label-mono">Pronunciation (optional)</label>
              <input
                data-testid="onboarding-pronunciation-input"
                value={hint} onChange={(e) => setHint(e.target.value)}
                placeholder={`e.g. spell "${name}" the way it sounds`}
                className="mt-2 w-full rounded-xl bg-[#0D0D0D] border border-white/10 px-4 py-3 text-sm outline-none focus:border-mono-400/60"
              />
            </div>

            <button
              data-testid="onboarding-pronunciation-next"
              onClick={() => setStep(2)}
              className="mt-7 w-full rounded-full py-3 font-medium text-[#000000] flex items-center justify-center gap-2"
              style={{ background: "linear-gradient(90deg, #FFFFFF, #C8C8C8)" }}
            >
              Sounds good <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="fade-in-up">
            <h2 className="font-heading text-2xl font-medium">Who do you want to talk to?</h2>
            <p className="text-slate-400 mt-2">You can switch anytime.</p>
            <div className="grid grid-cols-2 gap-4 mt-6">
              {[
                { key: "anna", name: "Anna", tag: "Warm · Intuitive", av: ANNA_AVATAR },
                { key: "sam", name: "Sam", tag: "Direct · Practical", av: SAM_AVATAR },
              ].map((a) => (
                <button
                  key={a.key}
                  data-testid={`onboarding-select-${a.key}`}
                  onClick={() => setAssistant(a.key)}
                  className={`rounded-2xl p-5 border transition-all ${assistant === a.key ? "border-mono-400/60 bg-mono-400/5" : "border-white/10 hover:border-white/20"}`}
                >
                  <img src={a.av} alt={a.name} className="w-16 h-16 rounded-full mx-auto object-cover border border-white/10" />
                  <div className="font-heading text-lg mt-3">{a.name}</div>
                  <div className="text-xs text-slate-400 mt-1">{a.tag}</div>
                </button>
              ))}
            </div>
            <button
              data-testid="onboarding-finish"
              onClick={finish} disabled={saving}
              className="mt-7 w-full rounded-full py-3 font-medium text-[#000000] flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ background: "linear-gradient(90deg, #FFFFFF, #C8C8C8)" }}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Start talking
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
